import { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useHabits,
  useClientHabits,
  Habit,
  HabitCategory,
  ProgressionType,
  HABIT_CATEGORIES,
  TRACKING_TYPES,
  FREQUENCY_OPTIONS,
} from "@/hooks/use-habits";
import { ProgressionConfig } from "./ProgressionConfig";
import { toast } from "sonner";
import {
  Loader2,
  Search,
  Droplets,
  Moon,
  Brain,
  Zap,
  Footprints,
  Utensils,
  HeartPulse,
  Flower2,
  Star,
  CheckCircle2,
  Check,
} from "lucide-react";
import { cn } from "@/lib/utils";

const CATEGORY_ICONS: Record<HabitCategory, React.ReactNode> = {
  hydration: <Droplets className="h-4 w-4" />,
  sleep: <Moon className="h-4 w-4" />,
  stress: <Brain className="h-4 w-4" />,
  energy: <Zap className="h-4 w-4" />,
  movement: <Footprints className="h-4 w-4" />,
  nutrition: <Utensils className="h-4 w-4" />,
  recovery: <HeartPulse className="h-4 w-4" />,
  mindfulness: <Flower2 className="h-4 w-4" />,
  custom: <Star className="h-4 w-4" />,
};

interface AssignHabitDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  existingHabitIds: string[];
  onSuccess: () => void;
}

export function AssignHabitDialog({
  open,
  onOpenChange,
  clientId,
  existingHabitIds,
  onSuccess,
}: AssignHabitDialogProps) {
  const { habits, loading: habitsLoading } = useHabits();
  const { assignHabit } = useClientHabits(clientId);

  const [search, setSearch] = useState("");
  const [selectedHabit, setSelectedHabit] = useState<Habit | null>(null);
  const [frequency, setFrequency] = useState("daily");
  const [targetValue, setTargetValue] = useState<string>("");
  const [saving, setSaving] = useState(false);

  // Progression state
  const [progressionEnabled, setProgressionEnabled] = useState(false);
  const [progressionType, setProgressionType] = useState<ProgressionType>("weekly");
  const [progressionIncrement, setProgressionIncrement] = useState<number>(0);
  const [progressionMaxTarget, setProgressionMaxTarget] = useState<number | undefined>(undefined);
  const [initialTarget, setInitialTarget] = useState<number>(0);

  // Filter out already assigned habits
  const availableHabits = useMemo(() => {
    return habits
      .filter((h) => !existingHabitIds.includes(h.id))
      .filter(
        (h) =>
          h.name.toLowerCase().includes(search.toLowerCase()) ||
          h.description?.toLowerCase().includes(search.toLowerCase())
      );
  }, [habits, existingHabitIds, search]);

  // Group by category
  const habitsByCategory = useMemo(() => {
    const grouped: Record<string, Habit[]> = {};
    availableHabits.forEach((habit) => {
      if (!grouped[habit.category]) {
        grouped[habit.category] = [];
      }
      grouped[habit.category].push(habit);
    });
    return grouped;
  }, [availableHabits]);

  const handleSelectHabit = (habit: Habit) => {
    setSelectedHabit(habit);
    // Pre-fill target from habit default
    if (habit.default_target) {
      setTargetValue(habit.default_target.toString());
      setInitialTarget(habit.default_target);
    } else {
      setTargetValue("");
      setInitialTarget(0);
    }
    // Reset progression when selecting new habit
    setProgressionEnabled(false);
    setProgressionType("weekly");
    setProgressionIncrement(0);
    setProgressionMaxTarget(undefined);
  };

  const handleAssign = async () => {
    if (!selectedHabit) return;

    setSaving(true);
    try {
      await assignHabit({
        habit_id: selectedHabit.id,
        client_id: clientId,
        frequency,
        target_value: progressionEnabled ? initialTarget : (targetValue ? Number(targetValue) : undefined),
        // Progression fields
        progression_enabled: progressionEnabled,
        progression_type: progressionEnabled ? progressionType : undefined,
        progression_increment: progressionEnabled ? progressionIncrement : undefined,
        progression_max_target: progressionEnabled ? progressionMaxTarget : undefined,
        initial_target: progressionEnabled ? initialTarget : undefined,
      });
      toast.success(`${selectedHabit.name} assigned to client`);
      onSuccess();
      // Reset state
      setSelectedHabit(null);
      setSearch("");
      setFrequency("daily");
      setTargetValue("");
      setProgressionEnabled(false);
      setProgressionType("weekly");
      setProgressionIncrement(0);
      setProgressionMaxTarget(undefined);
      setInitialTarget(0);
    } catch (error: any) {
      if (error.message?.includes("duplicate")) {
        toast.error("This habit is already assigned to this client");
      } else {
        toast.error("Failed to assign habit");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setSelectedHabit(null);
    setSearch("");
    setFrequency("daily");
    setTargetValue("");
    setProgressionEnabled(false);
    setProgressionType("weekly");
    setProgressionIncrement(0);
    setProgressionMaxTarget(undefined);
    setInitialTarget(0);
  };

  const showTargetField =
    selectedHabit && selectedHabit.tracking_type !== "boolean";

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Assign Habit</DialogTitle>
          <DialogDescription>
            Select a habit to assign to this client
          </DialogDescription>
        </DialogHeader>

        {!selectedHabit ? (
          <>
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search habits..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Habit List */}
            <ScrollArea className="flex-1 min-h-[300px] max-h-[400px] pr-4">
              {habitsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : availableHabits.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Flower2 className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="font-semibold mb-2">
                    {habits.length === existingHabitIds.length
                      ? "All habits assigned"
                      : "No matching habits"}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {habits.length === existingHabitIds.length
                      ? "This client has all available habits assigned"
                      : "Try a different search term"}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {Object.entries(habitsByCategory).map(([category, categoryHabits]) => (
                    <div key={category}>
                      <div className="flex items-center gap-2 mb-2 text-sm font-medium text-muted-foreground">
                        {CATEGORY_ICONS[category as HabitCategory]}
                        {HABIT_CATEGORIES[category as HabitCategory].label}
                      </div>
                      <div className="space-y-1">
                        {categoryHabits.map((habit) => (
                          <button
                            key={habit.id}
                            onClick={() => handleSelectHabit(habit)}
                            className={cn(
                              "w-full flex items-center justify-between p-3 rounded-lg border text-left",
                              "hover:bg-muted/50 transition-colors"
                            )}
                          >
                            <div>
                              <div className="font-medium text-sm">{habit.name}</div>
                              {habit.description && (
                                <div className="text-xs text-muted-foreground line-clamp-1">
                                  {habit.description}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="secondary" className="text-xs">
                                {TRACKING_TYPES[habit.tracking_type].label}
                              </Badge>
                              {habit.is_system_template && (
                                <Badge variant="outline" className="text-xs">
                                  Template
                                </Badge>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </>
        ) : (
          <>
            {/* Selected Habit Configuration */}
            <div className="space-y-4 py-2">
              {/* Selected habit display */}
              <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                <div className="flex items-center gap-3">
                  <div
                    className={`h-10 w-10 rounded-lg bg-muted flex items-center justify-center ${
                      HABIT_CATEGORIES[selectedHabit.category].color
                    }`}
                  >
                    {CATEGORY_ICONS[selectedHabit.category]}
                  </div>
                  <div>
                    <div className="font-medium">{selectedHabit.name}</div>
                    <div className="text-sm text-muted-foreground">
                      {HABIT_CATEGORIES[selectedHabit.category].label} •{" "}
                      {TRACKING_TYPES[selectedHabit.tracking_type].label}
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedHabit(null)}
                >
                  Change
                </Button>
              </div>

              {/* Frequency */}
              <div className="space-y-2">
                <Label>Frequency</Label>
                <Select value={frequency} onValueChange={setFrequency}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FREQUENCY_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Target Value */}
              {showTargetField && !progressionEnabled && (
                <div className="space-y-2">
                  <Label>
                    Target{" "}
                    {selectedHabit.unit && (
                      <span className="text-muted-foreground font-normal">
                        ({selectedHabit.unit})
                      </span>
                    )}
                  </Label>
                  <Input
                    type="number"
                    placeholder={
                      selectedHabit.default_target
                        ? `Default: ${selectedHabit.default_target}`
                        : "Enter target value"
                    }
                    value={targetValue}
                    onChange={(e) => setTargetValue(e.target.value)}
                    min={selectedHabit.tracking_type === "quality_score" ? 1 : 0}
                    max={selectedHabit.tracking_type === "quality_score" ? 10 : undefined}
                  />
                  {selectedHabit.tracking_type === "quality_score" && (
                    <p className="text-xs text-muted-foreground">
                      Quality score target (1-10)
                    </p>
                  )}
                </div>
              )}

              {/* Progressive Target Configuration */}
              {showTargetField && (
                <ProgressionConfig
                  enabled={progressionEnabled}
                  onEnabledChange={setProgressionEnabled}
                  type={progressionType}
                  onTypeChange={setProgressionType}
                  initialTarget={initialTarget}
                  onInitialTargetChange={setInitialTarget}
                  increment={progressionIncrement}
                  onIncrementChange={setProgressionIncrement}
                  maxTarget={progressionMaxTarget}
                  onMaxTargetChange={setProgressionMaxTarget}
                  unit={selectedHabit.unit}
                  trackingType={selectedHabit.tracking_type}
                />
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={handleAssign} disabled={saving} className="gap-2">
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                Assign Habit
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
